import React from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, Container, Element, Heading, Section } from 'react-bulma-components'
import { setTranslations } from 'react-i18nify'
import Layout from '../../components/layout/layout'
import { OptimizedImage } from '../../components/optimized-image'
import { Translate } from '../../utils/translations'
import { campaign } from '..'
import { useRecordInspection } from './api'
import { altText } from './alt-text'
import ComparisonPair from './comparison-pair'

/**
 * Recall notice: what the product is, what is wrong with it, and that the
 * reader should stop using it and go on to the inspection.
 *
 * Block order follows the campaign brief, which is the authority on sequence.
 * Every visible string comes from the translations sheet. The only authored
 * text is the image alt copy, which the sheet has no rows for yet.
 */
const SimondInfoPage: React.FC = () => {
  setTranslations(campaign.presentationLocales)
  const { lang } = useParams()
  const recordInspection = useRecordInspection()

  /**
   * Record that the reader went on to the inspection procedure.
   *
   * Paired with the outcome records, this gives Simond a funnel: how many got
   * far enough to read the instructions against how many completed a check. A
   * wide gap means the notice is not persuading people to inspect, which is the
   * page's whole purpose.
   *
   * Counted by volume, not by person — nothing identifies a device, so a reader
   * who opens the procedure twice appears twice. Not awaited, for the same
   * reason as the outcome: reporting must never delay the instructions.
   */
  const handleProcedureOpened = () => {
    recordInspection.mutate({
      data: { result: 'procedureOpened', language: lang ?? 'en' },
    })
  }

  return (
    <Layout>
      <Element className="simond">
        <Section>
          <Container className="simond__column">
            <Heading size={3} textAlign="center" className="simond__title" data-testid="recall-title">
              <Translate value="title" />
            </Heading>

            <Element className="simond__lead" mb={5}>
              <Translate value="homepage.notice" />
            </Element>

            <Element mb={4}>
              <Translate value="homepage.purchaseWindow" />
            </Element>

            <Element className="simond__skus" mb={5}>
              <Translate value="homepage.skus" />
            </Element>

            <Element mb={5}>
              <Translate value="homepage.responsibility" />
            </Element>

            <Element className="simond__product" mb={5}>
              <OptimizedImage
                imageName={campaign.productImageName}
                alt={campaign.productImageAlt}
                priority
                loading="eager"
              />
            </Element>

            <Element mb={5}>
              <Translate value="homepage.defect" />
            </Element>

            {/* The brief places the rivet reference images with the defect
                description: they explain what the defect is, whereas the images
                on the next screen guide the test itself. */}
            <ComparisonPair
              okImageName="rivet_ok"
              nokImageName="rivet_nok"
              okAlt={altText('rivetOk', lang)}
              nokAlt={altText('rivetNok', lang)}
            />

            <Element className="simond__warning" mb={6} data-testid="stop-using-warning">
              <Translate value="homepage.stopUsing" />
            </Element>

            <Element textAlign="center">
              <Button
                renderAs={Link}
                to={`/${lang}/check`}
                className="simond__cta"
                data-testid="info-cta"
                size="large"
                onClick={handleProcedureOpened}
              >
                <Translate value="procedure.title" />
              </Button>
            </Element>
          </Container>
        </Section>
      </Element>
    </Layout>
  )
}

export default SimondInfoPage
